// src/pages/tender/PaymentStatus.js

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import toast, { Toaster } from "react-hot-toast";
import dayjs from "dayjs";

const formatINR = (n) =>
  `₹ ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const fmtDate = (v) => {
  try {
    const d = v?.toDate ? v.toDate() : v ? new Date(v) : null;
    if (!d || Number.isNaN(d.getTime())) return "—";
    return dayjs(d).format("DD/MM/YYYY");
  } catch {
    return "—";
  }
};

function getRaAmount(r) {
  const basic = Number(r.basicAmount || r.totalAmount || r.grossAmount || 0);
  const gst = Number(r.gstAmount || 0);
  const gross = Number(r.grossWithGST || r.billAmount || r.invoiceAmount || basic + gst);
  const penalty = Number(r.penaltyAmount || 0);
  const deduction = Number(r.deductionAmount || 0);
  const tds = Number(r.tdsAmount || 0);
  const retention = Number(r.retentionAmount || 0);

  return Number(
    r.netPayable ||
      r.approvedAmount ||
      gross - penalty - deduction - tds - retention ||
      r.totalAmount ||
      0
  );
}

function getPaidAmount(r) {
  return Number(r.totalPaidAmount || r.allocatedPaidAmount || r.paymentAmount || r.paidAmount || 0);
}

function getOutstanding(r) {
  const amount = getRaAmount(r);
  const paid = getPaidAmount(r);

  if (r.outstandingAmount !== undefined) {
    return Number(r.outstandingAmount || 0);
  }

  return Math.max(amount - paid, 0);
}

function getPaymentStatus(r) {
  const amount = getRaAmount(r);
  const paid = getPaidAmount(r);
  const outstanding = getOutstanding(r);

  if (paid > 0 && outstanding <= 0) return "FULLY_PAID";
  if (paid > 0 && paid < amount) return "PART_PAID";
  return r.paymentStatus || "PENDING";
}

function Badge({ status }) {
  const s = String(status || "").toUpperCase();

  const cls =
    s === "FULLY_PAID" || s === "PAID" || s === "PAYMENT_VERIFIED"
      ? "bg-emerald-100 text-emerald-700 border-emerald-300"
      : s === "PART_PAID"
      ? "bg-blue-100 text-blue-700 border-blue-300"
      : s === "APPROVED" || s === "CEO_APPROVED"
      ? "bg-blue-100 text-blue-700 border-blue-300"
      : s === "PENDING"
      ? "bg-amber-100 text-amber-700 border-amber-300"
      : "bg-red-100 text-red-700 border-red-300";

  return (
    <span className={`rounded-full border px-2 py-1 text-[10px] font-black ${cls}`}>
      {status || "PENDING"}
    </span>
  );
}

function StatCard({ label, value, note }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-[11px] font-black uppercase text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-black text-slate-950">{value}</div>
      {note && <div className="text-[11px] text-slate-400">{note}</div>}
    </div>
  );
}

export default function PaymentStatus() {
  const { tenderId } = useParams();
  const navigate = useNavigate();
  const auth = useMemo(() => getAuth(), []);

  const [user, setUser] = useState(null);
  const [tender, setTender] = useState(null);
  const [raBills, setRaBills] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (!u) {
        toast.error("Please login first.");
        navigate("/tender/login", { replace: true });
        return;
      }
      setUser(u);
    });

    return () => unsub();
  }, [auth, navigate]);

  const loadData = async () => {
    if (!user || !tenderId) return;

    try {
      setLoading(true);

      const tSnap = await getDoc(doc(db, "tenders", tenderId));

      if (!tSnap.exists()) {
        toast.error("Project not found.");
        navigate("/tender/contractor?section=payments");
        return;
      }

      const t = { id: tSnap.id, ...tSnap.data() };

      if (t.awardedVendorUid && t.awardedVendorUid !== user.uid) {
        toast.error("This project is not assigned to your login.");
        navigate("/tender/contractor?section=payments");
        return;
      }

      setTender(t);

      const raSnap = await getDocs(
        query(
          collection(db, "ra_bills"),
          where("tenderId", "==", tenderId),
          where("vendorUid", "==", user.uid)
        )
      );

      const rows = [];
      raSnap.forEach((d) => {
        const r = { id: d.id, ...d.data() };
        const raAmount = getRaAmount(r);
        const paidAmount = getPaidAmount(r);
        const outstandingAmount = getOutstanding(r);
        const paymentStatus = getPaymentStatus(r);

        rows.push({
          ...r,
          raAmount,
          paidAmount,
          outstandingAmount,
          paymentStatus,
          lastPaymentVoucherNo: r.lastPaymentVoucherNo || r.paymentVoucherNo || "",
          lastPaymentUtr: r.lastPaymentUtr || r.paymentUtr || r.utrNo || r.paymentRef || r.referenceNo || "",
          lastPaymentDate: r.lastPaymentDate || r.paymentDate || r.paidAt || "",
        });
      });

      rows.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
      setRaBills(rows);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load payment status.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tenderId]);

  const summary = useMemo(() => {
    const approvedBills = raBills.filter(
      (r) =>
        String(r.status || "").toLowerCase() === "approved" ||
        r.accountingStatus === "CEO_APPROVED"
    );

    const totalRa = raBills.reduce((s, r) => s + Number(r.raAmount || 0), 0);

    const approvedAmount = approvedBills.reduce(
      (s, r) => s + Number(r.raAmount || 0),
      0
    );

    const paidAmount = approvedBills.reduce(
      (s, r) => s + Number(r.paidAmount || 0),
      0
    );

    const balanceAmount = approvedBills.reduce(
      (s, r) => s + Number(r.outstandingAmount || 0),
      0
    );

    const partPaidCount = approvedBills.filter((r) => r.paymentStatus === "PART_PAID").length;
    const fullyPaidCount = approvedBills.filter((r) => r.paymentStatus === "FULLY_PAID").length;

    return {
      totalRa,
      approvedAmount,
      paidAmount,
      balanceAmount,
      approvedCount: approvedBills.length,
      partPaidCount,
      fullyPaidCount,
    };
  }, [raBills]);

  const projectName = tender?.title || tender?.projectName || "Project";
  const pacsName = tender?.ownerCompany || tender?.pacsName || tender?.organization || "—";
  const district = tender?.city || tender?.district || "—";

  if (loading || !tender) {
    return (
      <>
        <Toaster position="top-center" />
        <div className="min-h-screen flex items-center justify-center bg-emerald-50">
          <div className="rounded-2xl bg-white px-8 py-5 shadow text-sm text-slate-600">
            Loading payment status...
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Toaster position="top-center" />

      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-sky-50 px-4 py-8">
        <div className="mx-auto max-w-7xl">
          <button
            type="button"
            onClick={() => navigate("/tender/contractor?section=payments")}
            className="mb-4 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-black text-slate-700 shadow-sm hover:bg-slate-50"
          >
            ← Back to Payments
          </button>

          <div className="overflow-hidden rounded-[2rem] border border-emerald-100 bg-white shadow-xl">
            <div className="bg-slate-950 px-6 py-6 text-white">
              <div className="text-[11px] font-black uppercase tracking-[0.25em] text-emerald-300">
                Contractor Payment Status
              </div>
              <h1 className="mt-2 text-2xl font-black leading-snug">{projectName}</h1>
              <div className="mt-2 text-xs text-slate-300">
                {pacsName} • {district} • Tender ID: {tenderId}
              </div>
            </div>

            <div className="grid gap-4 p-6 md:grid-cols-6">
              <StatCard label="Total RA Amount" value={formatINR(summary.totalRa)} note="All submitted RA bills" />
              <StatCard label="Approved Amount" value={formatINR(summary.approvedAmount)} note={`${summary.approvedCount} approved RA`} />
              <StatCard label="Paid Amount" value={formatINR(summary.paidAmount)} note="Payment processed" />
              <StatCard label="Balance Amount" value={formatINR(summary.balanceAmount)} note="Pending payment" />
              <StatCard label="Part Paid" value={summary.partPaidCount} note="Partial adjusted invoices" />
              <StatCard label="Fully Paid" value={summary.fullyPaidCount} note="Closed invoices" />
            </div>

            <div className="px-6 pb-6">
              <div className="overflow-x-auto rounded-2xl border border-slate-300">
                <table className="w-full min-w-[1450px] border-collapse text-[11px]">
                  <thead>
                    <tr className="bg-slate-950 text-white">
                      <th className="border border-slate-400 px-3 py-3 text-center">Sr</th>
                      <th className="border border-slate-400 px-3 py-3 text-left">RA / Invoice No</th>
                      <th className="border border-slate-400 px-3 py-3 text-center">RA Date</th>
                      <th className="border border-slate-400 px-3 py-3 text-right">RA Amount</th>
                      <th className="border border-slate-400 px-3 py-3 text-center">PMC Status</th>
                      <th className="border border-slate-400 px-3 py-3 text-center">Payment Status</th>
                      <th className="border border-slate-400 px-3 py-3 text-right">Paid Amount</th>
                      <th className="border border-slate-400 px-3 py-3 text-right">Outstanding</th>
                      <th className="border border-slate-400 px-3 py-3 text-center">Paid Date</th>
                      <th className="border border-slate-400 px-3 py-3 text-left">UTR / Ref No</th>
                      <th className="border border-slate-400 px-3 py-3 text-left">Voucher No</th>
                      <th className="border border-slate-400 px-3 py-3 text-left">Remarks</th>
                    </tr>
                  </thead>

                  <tbody>
                    {raBills.length === 0 ? (
                      <tr>
                        <td colSpan={12} className="border border-slate-300 px-3 py-6 text-center text-slate-500">
                          No RA bill found for this project.
                        </td>
                      </tr>
                    ) : (
                      raBills.map((r, idx) => {
                        const pmcStatus =
                          r.accountingStatus === "CEO_APPROVED"
                            ? "approved"
                            : r.status || "pending";

                        const paidDate = fmtDate(r.lastPaymentDate);

                        return (
                          <tr key={r.id} className="hover:bg-emerald-50">
                            <td className="border border-slate-300 px-3 py-3 text-center font-black">
                              {idx + 1}
                            </td>

                            <td className="border border-slate-300 px-3 py-3 font-black">
                              {r.raInvoiceNo || r.invoiceNo || r.raNo || r.billNo || `RA-${idx + 1}`}
                              <div className="text-[10px] text-slate-500">ID: {r.id}</div>
                            </td>

                            <td className="border border-slate-300 px-3 py-3 text-center">
                              {r.raDate || fmtDate(r.raInvoiceDate || r.invoiceDate || r.createdAt)}
                            </td>

                            <td className="border border-slate-300 px-3 py-3 text-right font-black">
                              {formatINR(r.raAmount)}
                            </td>

                            <td className="border border-slate-300 px-3 py-3 text-center">
                              <Badge status={pmcStatus}>{pmcStatus}</Badge>
                            </td>

                            <td className="border border-slate-300 px-3 py-3 text-center">
                              <Badge status={r.paymentStatus}>{r.paymentStatus}</Badge>
                            </td>

                            <td className="border border-slate-300 px-3 py-3 text-right font-black text-emerald-700">
                              {formatINR(r.paidAmount)}
                            </td>

                            <td className="border border-slate-300 px-3 py-3 text-right font-black text-red-600">
                              {formatINR(r.outstandingAmount)}
                            </td>

                            <td className="border border-slate-300 px-3 py-3 text-center">
                              {paidDate}
                            </td>

                            <td className="border border-slate-300 px-3 py-3 font-bold">
                              {r.lastPaymentUtr || "—"}
                            </td>

                            <td className="border border-slate-300 px-3 py-3 font-bold">
                              {r.lastPaymentVoucherNo || "—"}
                            </td>

                            <td className="border border-slate-300 px-3 py-3">
                              {r.paymentRemarks ||
                                r.remarks ||
                                (r.paymentStatus === "PART_PAID"
                                  ? "Partial payment adjusted"
                                  : r.paymentStatus === "FULLY_PAID"
                                  ? "Invoice fully paid"
                                  : "—")}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>

                  {raBills.length > 0 && (
                    <tfoot>
                      <tr className="bg-emerald-50 font-black">
                        <td colSpan={3} className="border border-slate-300 px-3 py-3 text-right">
                          TOTAL
                        </td>
                        <td className="border border-slate-300 px-3 py-3 text-right">
                          {formatINR(summary.totalRa)}
                        </td>
                        <td colSpan={2} className="border border-slate-300 px-3 py-3 text-right">
                          PAID / BALANCE
                        </td>
                        <td className="border border-slate-300 px-3 py-3 text-right text-emerald-700">
                          {formatINR(summary.paidAmount)}
                        </td>
                        <td className="border border-slate-300 px-3 py-3 text-right text-red-600">
                          {formatINR(summary.balanceAmount)}
                        </td>
                        <td colSpan={4} className="border border-slate-300 px-3 py-3 text-right text-amber-700">
                          Part Paid: {summary.partPaidCount} | Fully Paid: {summary.fullyPaidCount}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}